/**
 * ProofOfPaymentDialog — THE Proof of Payment window, shared.
 *
 * Born 2026-09-22 when the Sale Detail window needed it: two hand-typed copies
 * already existed (PaymentsPage, CustomerAccountPopup) and had started to drift.
 * This one is built from the fuller PaymentsPage version plus the method-detail
 * cards from the popup. Fetches the PP by number, lists every receipt it
 * settled, reprints (thermal / full page / branch printer).
 *
 * Props:
 *   open, onClose
 *   ppNumber           — "PP-MB-001132"
 *   onOpenReceipt(line) — optional; makes each receipt number a link. The caller
 *                         decides what "open" means (swap the Sale Detail,
 *                         open a modal, …). `line` = { invoice_id, invoice_number }
 *   currentInvoiceId   — optional; that receipt's row is highlighted "you are here"
 *   voidHref           — optional; when given, a "Void Entire PP" button links there
 *                         (the corrections door). Omitted = no void button.
 *   businessInfo       — optional; fetched here when not passed
 */
import { useEffect, useState } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { formatDate, formatDateTime } from '../lib/dateFormat';
import PrintEngine from '../lib/PrintEngine';
import SendToPrintModal from './SendToPrintModal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { FileCheck, RefreshCw, Ban, Send, Printer } from 'lucide-react';

export function ppLineStatus(l) {
  const pending = Number(l.pending_balance) || 0;
  const applied = Number(l.amount_applied) || 0;
  const disc = Number(l.discount) || 0;
  if (pending <= 0.005 && applied > 0) {
    return disc > 0
      ? { label: 'Fully Settled (with discount)', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
      : { label: 'Fully Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  }
  if (pending > 0.005 && applied > 0) {
    return { label: `Partial — ${formatPHP(pending)} remaining`, cls: 'bg-amber-100 text-amber-700 border-amber-200' };
  }
  if (applied === 0 && disc > 0) return { label: 'Discount Applied', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
  return { label: 'Recorded', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
}

/** Method details worth a card: check no. / PDC date / bank / ref / sender. */
export function ppMethodRows(pp) {
  const rows = [];
  const isCheck = String(pp?.method || '').toLowerCase() === 'check';
  if (isCheck) {
    const chk = (pp.check_number || '').trim() || (pp.reference || '').trim();
    if (chk) rows.push(['Check No.', chk]);
    if ((pp.bank_name || '').trim()) rows.push(['Bank', pp.bank_name]);
    if (pp.check_date) {
      const pdc = pp.date && String(pp.check_date).slice(0, 10) > String(pp.date).slice(0, 10);
      rows.push(['Check Date', pp.check_date + (pdc ? ' (PDC)' : '')]);
    }
  } else {
    if ((pp?.reference || '').trim()) rows.push(['Ref No.', pp.reference]);
    if ((pp?.bank_name || '').trim()) rows.push(['Bank', pp.bank_name]);
    if ((pp?.sender || '').trim()) rows.push(['Sender', pp.sender]);
  }
  return rows;
}

export default function ProofOfPaymentDialog({
  open, onClose, ppNumber, onOpenReceipt, currentInvoiceId, voidHref, businessInfo: bizProp,
}) {
  const [pp, setPp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [bizLocal, setBizLocal] = useState(null);
  const businessInfo = bizProp || bizLocal || {};

  useEffect(() => {
    if (!open || !ppNumber) return;
    let active = true;
    setPp(null); setError(''); setLoading(true);
    api.get(`/payment-receipts/${encodeURIComponent(ppNumber)}`)
      .then(r => { if (active) setPp(r.data); })
      .catch(e => { if (active) setError(e?.response?.data?.detail || 'Could not load this Proof of Payment.'); })
      .finally(() => { if (active) setLoading(false); });
    if (!bizProp && !bizLocal) {
      api.get('/settings/business-info').then(r => { if (active) setBizLocal(r.data || {}); }).catch(() => {});
    }
    return () => { active = false; };
  }, [open, ppNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const reprint = (format) => {
    if (!pp) return;
    PrintEngine.print({ type: 'proof_of_payment', data: pp, format, businessInfo });
  };

  const lines = pp?.lines || [];
  const totalDisc = lines.reduce((s, l) => s + (Number(l.discount) || 0), 0);
  const methodRows = pp ? ppMethodRows(pp) : [];

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }} modal={!sendOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pp-detail-modal"
          onInteractOutside={(e) => { if (sendOpen) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
              <FileCheck size={18} className="text-emerald-600" />
              Proof of Payment <span className="font-mono text-emerald-700">{ppNumber}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Supplementary, non-BIR document. Lists every invoice settled by this payment.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-500" data-testid="pp-detail-loading">
              <RefreshCw size={16} className="animate-spin mr-2" /> Loading payment breakdown…
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center justify-center py-10 text-rose-600" data-testid="pp-detail-error">{error}</div>
          )}
          {!loading && !error && pp && (
            <div className="space-y-3" data-testid="pp-detail-body">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Date</div>
                  <div className="text-slate-800 font-medium">{formatDate(pp.payment_date || pp.date) || '—'}</div>
                  {pp.payment_date && pp.date && pp.payment_date !== pp.date && (
                    <div className="text-[10px] text-slate-500 mt-0.5">Posted {formatDate(pp.date)}</div>
                  )}
                  {pp.created_at && <div className="text-[10px] text-slate-500 mt-0.5">Recorded {formatDateTime(pp.created_at)}</div>}
                </div>
                <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Customer</div>
                  <div className="text-slate-800 font-medium truncate" title={pp.customer_name}>{pp.customer_name || '—'}</div>
                </div>
                <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Method</div>
                  <div className="text-slate-800 font-medium">{pp.method || '—'}</div>
                  {pp.recorded_by && <div className="text-[10px] text-slate-500 mt-0.5 truncate">by {pp.recorded_by}</div>}
                </div>
                <div className="bg-emerald-50 rounded-md px-3 py-2 border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Total Applied</div>
                  <div className="text-emerald-800 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(pp.total_amount)}</div>
                </div>
              </div>

              {methodRows.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="pp-method-details">
                  {methodRows.map(([label, value]) => (
                    <div key={label} className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                      <div className="text-slate-800 font-medium truncate">{value}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-xs" data-testid="pp-detail-lines-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Receipt No.</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Original Amount</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Outstanding at Payment</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Amount Applied</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 ? (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No allocation lines on this Proof of Payment.</td></tr>
                    ) : lines.map((l, i) => {
                      const st = ppLineStatus(l);
                      const isHere = !!currentInvoiceId && l.invoice_id === currentInvoiceId;
                      return (
                        <tr key={i} className={`border-b border-slate-100 last:border-0 ${isHere ? 'bg-blue-50/60' : 'hover:bg-slate-50/60'}`} data-testid={`pp-detail-line-${i}`}>
                          <td className="px-3 py-2">
                            {onOpenReceipt && l.invoice_id && !isHere ? (
                              <button onClick={() => onOpenReceipt(l)} className="font-mono text-blue-700 hover:underline"
                                title="Open this receipt" data-testid={`pp-detail-line-invoice-${i}`}>
                                {l.invoice_number}
                              </button>
                            ) : (
                              <span className="font-mono text-slate-700">{l.invoice_number}</span>
                            )}
                            {isHere && <span className="ml-2 text-[10px] text-blue-700" data-testid="pp-you-are-here">← you are here</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(l.original_amount)}</td>
                          <td className="px-3 py-2 text-right text-slate-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(l.balance_before)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatPHP(l.amount_applied)}
                            {Number(l.discount) > 0 && <div className="text-[10px] font-normal text-blue-600">+{formatPHP(l.discount)} disc</div>}
                          </td>
                          <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {lines.length > 0 && (
                    <tfoot className="bg-slate-50 border-t border-slate-200">
                      <tr className="text-xs">
                        <td className="px-3 py-2 text-right font-semibold text-slate-600" colSpan={3}>Totals</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatPHP(pp.total_amount)}
                          {totalDisc > 0 && <div className="text-[10px] font-normal text-blue-600">+{formatPHP(totalDisc)} total disc</div>}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {pp.voided && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="pp-voided-banner">
                  <Ban size={13} className="shrink-0" />
                  <span><strong>Voided</strong>{pp.voided_by ? ` by ${pp.voided_by}` : ''}{pp.void_reason ? ` — ${pp.void_reason}` : ''}</span>
                </div>
              )}

              {Array.isArray(pp.date_edit_history) && pp.date_edit_history.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs space-y-1" data-testid="pp-date-corrections">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Date corrections</div>
                  {pp.date_edit_history.map((h, i) => (
                    <div key={i} className="text-amber-900">
                      <span className="font-medium">{formatDate(h.from)} → {formatDate(h.to)}</span>
                      <span className="text-amber-700"> · {formatDateTime(h.edited_at)}</span>
                      {h.edited_by && <span className="text-amber-700"> · by {h.edited_by}</span>}
                      {h.authorized_by && h.authorized_by !== h.edited_by && <span className="text-amber-700"> (authorized: {h.authorized_by})</span>}
                      {h.reason && <div className="text-[10px] text-amber-700 italic">"{h.reason}"</div>}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-between pt-1 border-t border-slate-100">
                <div className="flex gap-2">
                  {voidHref && !pp.voided && (
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => { window.location.href = voidHref; }} data-testid="void-pp-btn">
                      <Ban size={12} className="mr-1.5" /> Void Entire PP
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    onClick={() => setSendOpen(true)} data-testid="pp-detail-send-print-btn">
                    <Send size={12} className="mr-1.5" /> Send to Branch Printer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reprint('thermal')} data-testid="pp-detail-reprint-thermal-btn">
                    <Printer size={12} className="mr-1.5" /> Reprint Thermal
                  </Button>
                  <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" onClick={() => reprint('full_page')} data-testid="pp-detail-reprint-btn">
                    <Printer size={12} className="mr-1.5" /> Reprint Proof of Payment
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {pp && (
        <SendToPrintModal
          open={sendOpen}
          onOpenChange={setSendOpen}
          documentType="proof_of_payment"
          receiverDocType="proof_of_payment"
          documentName={`Proof of Payment #${pp.receipt_number || ppNumber || ''}`}
          documentId={pp.id || pp.receipt_number || ''}
          referenceNumber={pp.receipt_number || ppNumber || ''}
          branchId={pp.branch_id || ''}
          buildHtml={(format) => PrintEngine.generateHtml({ type: 'proof_of_payment', data: pp, format, businessInfo })}
          metadata={{ receipt_number: pp.receipt_number, customer_name: pp.customer_name }}
        />
      )}
    </>
  );
}
